import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { INBOUND_DIMENSION_LABELS } from '../constants';

import { buildOverlaidColumnChart, OVERLAID_COLUMN_CHART_HEIGHT } from './overlaidColumnChart';

type Props = {
  loading: boolean;
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Admitted rate tile: admission conversion (% of selected, % of received) +
// awaiting-admission count, over an overlaid "selected vs admitted" bar per View-By entity. Admitted
// is a strict subset of selected — the referral's Selected outcome created a stay that later reached
// Admitted & In Treatment.
export default function AdmissionDetailHeader({ loading, initialLoading, rollups, data }: Props) {
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);

  const overall = rollups.overall.metrics;
  const admissionRate = overall.getCurrentDataPoint(Metrics.ADMISSION_RATE, 0);
  const admissionRateOfReceived = overall.getCurrentDataPoint(Metrics.ADMISSION_RATE_OF_RECEIVED, 0);
  const admittedCount = overall.getCurrentDataPoint(Metrics.ADMITTED_COUNT, 0) ?? 0;
  const selectedCount = overall.getCurrentDataPoint(Metrics.SELECTED_COUNT, 0) ?? 0;
  const receivedCount = overall.getCurrentDataPoint(Metrics.REFERRAL_COUNT, 0) ?? 0;
  const awaitingCount = overall.getCurrentDataPoint(Metrics.AWAITING_ADMISSION_COUNT, 0) ?? 0;

  const entityLabel = slideoutDimension ? (INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '') : '';

  // Sort by admission rate desc — matches the breakdown table's default sort.
  const { chartOptions, scrollConfig, isEmpty } = useMemo(
    () =>
      buildOverlaidColumnChart({
        names: data.dimensions.getCurrentDimensionValuesByIndex(0),
        rates: data.metrics.getCurrentSeries(Metrics.ADMISSION_RATE),
        baseSeries: { name: 'Total Selected', values: data.metrics.getCurrentSeries(Metrics.SELECTED_COUNT) },
        overlaySeries: {
          name: 'Total Admitted',
          values: data.metrics.getCurrentSeries(Metrics.ADMITTED_COUNT),
          color: colors.blue100,
        },
      }),
    [data]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='% of Selected'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(admissionRate)}
            addendum={admissionRate != null ? `${admittedCount} admitted of ${selectedCount} selected` : undefined}
          />
          <StatTile
            label='% of Received'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(admissionRateOfReceived)}
            addendum={
              admissionRateOfReceived != null ? `${admittedCount} admitted of ${receivedCount} received` : undefined
            }
          />
          <StatTile
            label='Selected Referrals Awaiting Admission'
            loading={initialLoading}
            primary={formatWholeNumber(awaitingCount)}
          />
        </>
      }
      chartTitle={`Admission Rate by ${entityLabel}`}
      chartLoading={loading}
      chartHeight={OVERLAID_COLUMN_CHART_HEIGHT}
      isEmpty={isEmpty}
      scrollConfig={scrollConfig}
      emptyMessage='No admitted referrals to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
